/**
 * InterlockScaffold — the page shell shared by every /interlock screen:
 * the NavRail (left) + a stage (right) with a topbar (title / subtitle / actions)
 * and a body slot. Renders its own chrome — no legacy FMEA Studio shell.
 *
 * Also hosts the shared DrPanel (Wave 3 parcel L1): any page can deep-link a DR
 * by navigating to `?dr=DR-01` (see `DrChip` in primitives.tsx) and this shell
 * mounts/unmounts the panel on top of whatever page it lands on. Removing the
 * `dr` param (✕ / scrim-click / Escape) closes it without touching other
 * search params on the URL.
 */
import type { ReactNode } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { NavRail, type NavKey } from './NavRail';
import { DrPanel } from './DrPanel';
import { LiveRegion } from './liveRegion';
import { ProjectSwitcher } from './ProjectSwitcher';
import './interlock.css';

export function InterlockScaffold({
  nav,
  title,
  titleSmall,
  revTag,
  subtitle,
  actions,
  children,
  onDrMutated,
}: {
  nav?: NavKey;
  title: string;
  titleSmall?: boolean;
  revTag?: string;
  subtitle?: ReactNode;
  actions?: ReactNode;
  children: ReactNode;
  /** G4 — the shared DrPanel is mounted here, not inside the page; a page that
   * needs to recompute on a DR answer (e.g. GatePage) passes its own refetch. */
  onDrMutated?: () => void;
}) {
  const [searchParams, setSearchParams] = useSearchParams();
  const drRefId = searchParams.get('dr');
  const closeDrPanel = () => {
    const next = new URLSearchParams(searchParams);
    next.delete('dr');
    setSearchParams(next, { replace: true });
  };

  return (
    <div className="ilk-app">
      {/* A1 — one polite live region per shell; toast/bell code calls announce()
          from components/interlock/liveRegion.tsx. */}
      <LiveRegion />
      <NavRail active={nav} />
      <div className="ilk-stage">
        <div className="ilk-topbar">
          <div className="ilk-topbar-titles">
            <div className="ilk-topbar-titlerow">
              <h1 className={`ilk-h1${titleSmall ? ' ilk-h1-sm' : ''}`}>{title}</h1>
              {revTag && <span className="ilk-rev-tag">{revTag}</span>}
            </div>
            {subtitle && <div className="ilk-subtitle">{subtitle}</div>}
          </div>
          {/* Multi-project switcher — renders only when >1 project exists, so a
              single-project deployment sees no change (ProjectSwitcher.tsx). */}
          <ProjectSwitcher />
          {actions && <div className="ilk-topbar-actions">{actions}</div>}
        </div>
        {/* `display: contents` so the landmark costs nothing in the flex layout
            (`.ilk-stage`'s existing children keep participating in its flex
            column exactly as before). W3 removed the skip link that used to
            target a focus-anchor span in here; `<main>` stays because it is the
            real landmark AT uses for landmark navigation, and it is invisible. */}
        <main aria-label="Main content" style={{ display: 'contents' }}>
          {children}
        </main>
      </div>
      {drRefId && <DrPanel refId={drRefId} onClose={closeDrPanel} onMutated={onDrMutated} />}
    </div>
  );
}

/** The Grid / List / System view switcher used in interface-page topbars.
 * B4 — the register is a projection of the matrix page now
 * (/interlock/matrix?view=list), so the old "☰ Register" tab became "☰ List"
 * pointing there; the matrix page itself renders its own on-page toggle
 * (buttons, replace-not-push) instead of this link strip.
 *
 * Persona-audit Loop 1 (MINOR) — the tabs were hardcoded absolute paths, so
 * clicking any of them dropped every other query param on the URL, including
 * the dev `?actAs=<id>` impersonation hatch (interlockActor.ts) and any
 * `?scope=`/`?parent=` drill state. Carry the CURRENT search params across
 * the switch, only touching `view` (the one param this switcher itself
 * owns) — same "preserve, don't clobber" idiom as the page-local toggles
 * (SystemViewPage's setListMode / setShowAllComponents). */
export function ViewSwitcher({ active }: { active: 'register' | 'matrix' | 'system' }) {
  const [searchParams] = useSearchParams();

  const toFor = (key: 'register' | 'matrix' | 'system', pathname: string): string => {
    const next = new URLSearchParams(searchParams);
    if (key === 'register') next.set('view', 'list');
    else next.delete('view');
    const qs = next.toString();
    return qs ? `${pathname}?${qs}` : pathname;
  };

  const tabs: { key: 'register' | 'matrix' | 'system'; label: string; pathname: string }[] = [
    { key: 'matrix', label: '▦ Grid', pathname: '/interlock/matrix' },
    { key: 'register', label: '☰ List', pathname: '/interlock/matrix' },
    { key: 'system', label: '⬡ System', pathname: '/interlock/system' },
  ];
  return (
    <>
      {tabs.map((t) => (
        <Link
          key={t.key}
          to={toFor(t.key, t.pathname)}
          className={`ilk-viewtab${t.key === active ? ' is-active' : ''}`}
        >
          {t.label}
        </Link>
      ))}
    </>
  );
}
